import { useId } from 'react'
import { useT } from '_metronic/i18n/Metronici18n'
import { QuebraPorChave } from '../../core/_models'
import { formatarContagem } from '../../core/formatacao'
import { AjudaIndicador, montarNotasAjuda } from '../ajuda/AjudaIndicador'
import { IconPaths, KTSVG } from '_metronic/helpers'

type Props = {
  /** Título da tabela, exibido como `caption` (chrome de tela, vem do i18n). */
  titulo: string
  /** Cabeçalho da coluna de categoria. */
  colunaCategoria: string
  /** Cabeçalho da coluna de valor — "Interrupções" e "Atendimentos" contam coisas diferentes. */
  colunaValor: string
  dados: QuebraPorChave
  /** Prefixo das chaves de i18n que rotulam as chaves de enum do payload. */
  prefixoRotulo: string
  /** Valores de interpolação do rótulo (ex.: as horas que separam tardio de com antecedência). */
  valoresRotulo?: Record<string, string | number>
  /** Nota metodológica vinda da API (`rotulos.*`): vai para o `?` do título, omitida quando vazia. */
  nota?: string
  /** Frase do estado vazio (todas as chaves zeradas); sem ela, usa a genérica do painel. */
  textoVazio?: string
}

/**
 * Quebra do contrato em tabela acessível. Regras que não são estéticas:
 * havendo ao menos um valor, toda chave do payload vira linha, **inclusive as
 * zeradas** (esconder linha seria a tela decidindo o que o servidor já decidiu),
 * na ordem do payload. Chave sem rótulo de i18n exibe a chave crua — nunca some
 * da tabela nem ganha texto inventado.
 *
 * Com **todas** as chaves zeradas, a tabela dá lugar a um estado vazio escrito:
 * o zero continua dito (é medição feita, não ausência de dado), sem uma grade de
 * zeros que não informa nada além disso.
 */
export const TabelaQuebra = ({
  titulo,
  colunaCategoria,
  colunaValor,
  dados,
  prefixoRotulo,
  valoresRotulo,
  nota,
  textoVazio,
}: Props) => {
  const { t } = useT()
  const idBase = useId()
  const idTitulo = `${idBase}-titulo`
  const linhas = Object.entries(dados ?? {})
  const notas = montarNotasAjuda(idBase, [nota])
  const tudoZerado = linhas.every(([, valor]) => valor === 0)

  if (linhas.length === 0) return null

  return (
    <div className='mt-6'>
      <div className='d-flex justify-content-between align-items-start gap-2 mb-3'>
        <p id={idTitulo} className='fs-6 fw-semibold text-gray-900 mb-0'>
          {titulo}
        </p>
        <AjudaIndicador rotulo={t('indicadores.ajuda.rotulo', { titulo })} notas={notas} />
      </div>

      {tudoZerado ? (
        <p className='d-flex align-items-center fs-7 text-gray-700 mb-0'>
          <KTSVG path={IconPaths.CircleCheck} className='svg-icon-em me-2 fs-5' ariaHidden />
          <span>{textoVazio ?? t('indicadores.quebra.vazio')}</span>
        </p>
      ) : (
        <div className='table-responsive'>
          <table
            className='table table-row-bordered align-middle mb-0'
            aria-labelledby={idTitulo}
            aria-describedby={notas.length > 0 ? notas.map((n) => n.id).join(' ') : undefined}
          >
            <thead>
              <tr className='fs-7 fw-semibold text-gray-700'>
                <th scope='col'>{colunaCategoria}</th>
                <th scope='col' className='text-end'>
                  {colunaValor}
                </th>
              </tr>
            </thead>
            <tbody>
              {linhas.map(([chave, valor]) => (
                <tr key={chave}>
                  <th scope='row' className='fs-7 fw-normal text-gray-900'>
                    {t(`${prefixoRotulo}.${chave}`, valoresRotulo, chave)}
                  </th>
                  <td className='fs-7 fw-semibold text-gray-900 text-end'>
                    {formatarContagem(valor)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
